import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useMemo, useRef } from "react";
import { z } from "zod";

import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import type { CustomerDetails } from "~/shared/schemas/customer-schema";
import type { IdName } from "~/shared/schemas/generic-schema";
import type {
	LoadsArray,
	ShipmentStats,
	ShipmentStatusArray,
} from "~/shared/schemas/loads-schema.ts";
import {
	loadsArraySchema,
	loadSchema,
	shipmentStatsSchema,
	shipmentStatusArraySchema,
} from "~/shared/schemas/loads-schema.ts";
import {
	getAndValidate,
	getAndValidateTraxAr,
} from "~/shared/services/api/fetch-client.ts";
import {
	getFormattedLoadStatus,
	getFormattedLoadStatusExternal,
} from "~/shared/utils/load-status-fns.ts";

const paginatedLoadsSchema = z.object({
	items: loadSchema.array(),
	nextCursor: z.string().nullable(),
	hasMore: z.boolean(),
});

type PaginatedLoadsResponse = z.infer<typeof paginatedLoadsSchema>;

const formatAddress = (
	name: string | null | undefined,
	city: string | null | undefined,
	state: string | null | undefined,
	zip: string | null | undefined,
): string => {
	const cityStateZip = [city, state].filter(Boolean).join(", ");
	const withZip = [cityStateZip, zip].filter(Boolean).join(" ");
	return [name, withZip].filter(Boolean).join(", ");
};

export const defaultSummary: ShipmentStats = {
	todaysShipmentsTotal: 0,
	todaysShipmentsNotDispatched: {
		count: 0,
		exceptionsCount: 0,
	},
	todaysShipmentsDispatched: {
		count: 0,
		exceptionsCount: 0,
	},
	todaysShipmentsInTransit: {
		count: 0,
		exceptionsCount: 0,
	},
	todaysShipmentsDelivered: {
		count: 0,
		exceptionsCount: 0,
	},
	futureShipmentsTotal: 0,
	futureShipmentsNotDispatched: 0,
	futureShipmentsDispatched: 0,
	totalActiveShipments: 0,
	shipmentsNeedingAttention: 0,
	totalWeight: 0,
	uniqueCarriersCount: 0,
	isDefault: true,
};

type LoadsData = {
	summary: ShipmentStats;
	loads: LoadsArray;
};

type UseLoadsDataReturn = {
	loadsData: LoadsData;
	isLoading: boolean;
	isFetching: boolean;
	isFetchingLoads: boolean;
	isFetchingNextPage: boolean;
	hasNextPage: boolean;
	fetchNextPage: () => void;
	error: {
		errorSummary: Error | null | undefined;
		errorLoads: Error | null | undefined;
	};
	refetch: {
		summary: () => void;
		loads: () => void;
		combined: () => void;
	};
};

const getLoadsSummary = async (
	customers: IdName[],
	signal?: AbortSignal,
	isInternal?: boolean,
): Promise<ShipmentStats> => {
	const searchParams = new URLSearchParams();
	for (const customer of customers) {
		searchParams.append("customerIds", customer.id.toString());
	}

	const url = "loads/summary";

	const signalOptions = signal ? { signal } : undefined;

	const result = await (isInternal
		? getAndValidateTraxAr(
				`${url}?${searchParams}`,
				shipmentStatsSchema,
				signalOptions,
			)
		: await getAndValidate(url, shipmentStatsSchema, {
				signal: signal,
			}));

	if (!("data" in result)) {
		throw new Error("Failed to fetch loads summary");
	}

	return result.data;
};

const getLoadsData = async (
	searchParams: URLSearchParams,
	signal?: AbortSignal,
	isInternal?: boolean,
): Promise<LoadsArray> => {
	const queryString = searchParams.toString();
	const url = queryString ? `loads?${queryString}` : "loads";

	const signalOptions = signal ? { signal } : undefined;

	const result = isInternal
		? await getAndValidateTraxAr(url, loadsArraySchema, signalOptions)
		: await getAndValidate(url, loadsArraySchema, {
				signal: signal,
			});
	if (!("data" in result)) {
		throw new Error("Failed to fetch loads data");
	}

	return result.data;
};

const getLoadsDataV2 = async (
	searchParams: URLSearchParams,
	cursor: string | null,
	signal?: AbortSignal,
	pageSize = 50,
): Promise<PaginatedLoadsResponse> => {
	const params = new URLSearchParams(searchParams);
	params.set("pageSize", String(pageSize));
	if (cursor) {
		params.set("cursor", cursor);
	}
	const url = `loads/v2?${params}`;

	const signalOptions = signal ? { signal } : undefined;

	const result = await getAndValidateTraxAr(
		url,
		paginatedLoadsSchema,
		signalOptions,
	);
	if (!("data" in result)) {
		throw new Error("Failed to fetch loads data v2");
	}

	return result.data;
};

const toDateOrNull = (value: string | null | undefined): Date | null => {
	if (!value) return null;
	const parsed = new Date(value);
	return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export function useLoadsData({
	selectedCustomers,
	shipmentFilter,
	ltlBrokerIds = [],
	primaryBrokerIds = [],
	pageSize = 50,
	columnFilters,
	lookbackDays,
}: {
	selectedCustomers: IdName[];
	shipmentFilter: {
		status: number | null;
		needsImmediateAction: boolean;
		needsAcknowledgment: boolean;
	};
	ltlBrokerIds?: number[];
	primaryBrokerIds?: number[];
	pageSize?: number;
	columnFilters?: Record<string, string>;
	lookbackDays?: number;
}): UseLoadsDataReturn {
	const { isInternal } = useDeploymentType();

	const searchParams = useMemo(() => {
		const params = new URLSearchParams();

		if (lookbackDays) {
			params.append("lookbackDays", lookbackDays.toString());
		}

		// Add each customer ID as a separate parameter
		for (const customer of selectedCustomers) {
			params.append("customerIds", customer.id.toString());
		}

		for (const id of ltlBrokerIds) {
			params.append("ltlBrokerIds", id.toString());
		}
		for (const id of primaryBrokerIds) {
			params.append("primaryBrokerIds", id.toString());
		}

		const filters = {
			status:
				shipmentFilter.status == null
					? undefined
					: String(shipmentFilter.status),
			isImmediateAction: shipmentFilter.needsImmediateAction
				? String(shipmentFilter.needsImmediateAction)
				: undefined,
			isNeedsAcknowledged: shipmentFilter.needsAcknowledgment
				? String(shipmentFilter.needsAcknowledgment)
				: undefined,
		};
		for (const [key, value] of Object.entries(filters)) {
			if (value) {
				params.append(key, String(value));
			}
		}

		// Column-level filters typed into the grid filter row are sent as
		// individual query params (e.g. `?customerName=acme`) so the
		// backend can return a server-filtered page. Sort the keys so the
		// resulting query string (and therefore the React Query cache key)
		// stays stable regardless of insertion order.
		if (columnFilters) {
			for (const [key, value] of Object.entries(columnFilters).sort(
				([a], [b]) => a.localeCompare(b),
			)) {
				params.append(key, value);
			}
		}
		return params;
	}, [
		selectedCustomers,
		ltlBrokerIds,
		primaryBrokerIds,
		shipmentFilter.needsImmediateAction,
		shipmentFilter.needsAcknowledgment,
		shipmentFilter.status,
		columnFilters,
		lookbackDays,
	]);

	const {
		data: summaryData,
		isLoading: isLoadingSummary,
		isFetching: isFetchingSummary,
		error: errorSummary,
		refetch: refetchSummary,
	} = useQuery({
		queryKey: ["loadsSummary", selectedCustomers.map((c) => c.id).join(",")],
		queryFn: async ({ signal }) => {
			if (selectedCustomers.length === 0) {
				throw new Error("No customers selected");
			}
			return getLoadsSummary(selectedCustomers, signal, isInternal);
		},
		enabled: isInternal && selectedCustomers.length > 0,
		// Short gcTime keeps memory bounded: every distinct filter combination
		// creates its own cache entry, so retaining each for 5 minutes meant a
		// user typing/clearing filters held multiple full datasets in RAM.
		gcTime: 60 * 1000,
		refetchOnWindowFocus: false,
		refetchOnMount: false,
		refetchOnReconnect: false,
	});

	// --- Internal: cursor-based infinite scrolling via loads/v2 ---
	const internalEnabled =
		isInternal &&
		(selectedCustomers.length > 0 ||
			ltlBrokerIds.length > 0 ||
			primaryBrokerIds.length > 0);

	const {
		data: infiniteData,
		isLoading: isLoadingInfinite,
		isFetching: isFetchingInfinite,
		isFetchingNextPage,
		hasNextPage,
		fetchNextPage,
		error: errorInfinite,
		refetch: refetchInfinite,
	} = useInfiniteQuery({
		queryKey: [
			"loadsV2",
			selectedCustomers.map((c) => c.id).join(","),
			searchParams.toString(),
		],
		queryFn: async ({ signal, pageParam }) => {
			if (
				selectedCustomers.length === 0 &&
				ltlBrokerIds.length === 0 &&
				primaryBrokerIds.length === 0
			) {
				throw new Error(
					"Provide at least one customer, LTL broker, or primary broker",
				);
			}
			return getLoadsDataV2(searchParams, pageParam, signal, pageSize);
		},
		initialPageParam: null as string | null,
		getNextPageParam: (lastPage) =>
			lastPage.hasMore ? lastPage.nextCursor : undefined,
		enabled: internalEnabled,
		gcTime: 60 * 1000,
		refetchOnWindowFocus: false,
		refetchOnMount: false,
		refetchOnReconnect: false,
	});

	// --- External: single-fetch via old loads endpoint ---
	const externalEnabled = !isInternal && selectedCustomers.length > 0;

	const {
		data: externalLoads,
		isLoading: isLoadingExternal,
		isFetching: isFetchingExternal,
		error: errorExternal,
		refetch: refetchExternal,
	} = useQuery({
		queryKey: [
			"loads",
			selectedCustomers.map((c) => c.id).join(","),
			searchParams.toString(),
		],
		queryFn: async ({ signal }) => {
			return getLoadsData(searchParams, signal, false);
		},
		enabled: externalEnabled,
		gcTime: 60 * 1000,
		refetchOnWindowFocus: false,
		refetchOnMount: false,
		refetchOnReconnect: false,
	});

	// Cache processed pages so we only map new ones on each fetch
	const processedPagesRef = useRef<{
		pageCount: number;
		items: LoadsArray;
	}>({ pageCount: 0, items: [] });
	// Track the searchParams identity that produced the cached items so we
	// can invalidate the cache when React Query swaps to a different cached
	// query (e.g. user typed a filter then cleared it, returning to a
	// previously cached unfiltered result whose pages.length happens to
	// match). Without this, the memo's `pages.length` dep doesn't change
	// and the stale mapped items are returned.
	const cachedSearchParamsRef = useRef<string>("");

	const searchParamsString = searchParams.toString();

	const loads = useMemo(() => {
		if (!isInternal) {
			return (externalLoads ?? []).map((load) => ({
				...load,
				formattedPickupAddress: formatAddress(
					load.firstPickName,
					load.firstPickCity,
					load.firstPickState,
					load.firstPickZIP,
				),
				formattedDeliveryAddress: formatAddress(
					load.lastDropName,
					load.lastDropCity,
					load.lastDropState,
					load.lastDropZIP,
				),
				// "City, ST" from the most recent known location. filter(Boolean)
				// yields "" when both parts are absent (blank cell) and omits the
				// comma when only one part is present.
				formattedLastLocation: [load.lastLocationCity, load.lastLocationState]
					.filter(Boolean)
					.join(", "),
				// External (LTL Quoting) returns `pickupDate`; internal (TraxAR)
				// returns `loadDate`. Coalesce so `loadDateValue` (the Date used
				// for the grid's pickup-date column sort/filter and for the
				// default load order) is populated regardless of deployment.
				loadDateValue: toDateOrNull(load.pickupDate ?? load.loadDate),
				deliveryDateValue: toDateOrNull(load.deliveryDate),
				formattedStatus: getFormattedLoadStatusExternal(load.loadStatus),
			}));
		}

		const pages = infiniteData?.pages ?? [];
		const currentPageCount = pages.length;
		const currentSearchParams = searchParamsString;
		const cached = processedPagesRef.current;

		// Full reset: query key changed (different cached query swapped in)
		// or pages were invalidated.
		if (
			currentSearchParams !== cachedSearchParamsRef.current ||
			currentPageCount < cached.pageCount
		) {
			processedPagesRef.current = { pageCount: 0, items: [] };
			cachedSearchParamsRef.current = currentSearchParams;
		}

		// Only process pages we haven't seen yet
		const alreadyProcessed = processedPagesRef.current.items;
		const startIndex = processedPagesRef.current.pageCount;

		if (currentPageCount === startIndex) {
			return alreadyProcessed;
		}

		const newPages = pages.slice(startIndex);
		const newItems = newPages.flatMap((page) =>
			page.items.map((load) => ({
				...load,
				formattedPickupAddress: formatAddress(
					load.firstPickName,
					load.firstPickCity,
					load.firstPickState,
					load.firstPickZIP,
				),
				formattedDeliveryAddress: formatAddress(
					load.lastDropName,
					load.lastDropCity,
					load.lastDropState,
					load.lastDropZIP,
				),
				// "City, ST" from the most recent known location. filter(Boolean)
				// yields "" when both parts are absent (blank cell) and omits the
				// comma when only one part is present.
				formattedLastLocation: [load.lastLocationCity, load.lastLocationState]
					.filter(Boolean)
					.join(", "),
				// External (LTL Quoting) returns `pickupDate`; internal (TraxAR)
				// returns `loadDate`. Coalesce so `loadDateValue` (the Date used
				// for the grid's pickup-date column sort/filter and for the
				// default load order) is populated regardless of deployment.
				loadDateValue: toDateOrNull(load.pickupDate ?? load.loadDate),
				deliveryDateValue: toDateOrNull(load.deliveryDate),
				formattedStatus: getFormattedLoadStatus(load.loadStatus),
			})),
		);

		const combined = [...alreadyProcessed, ...newItems];
		processedPagesRef.current = {
			pageCount: currentPageCount,
			items: combined,
		};
		return combined;
		// eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally depend on pages.length (not pages) so the memo only re-runs when a new page is appended, not on every infinite-query refetch. searchParamsString is included so a query-key change (e.g. filter cleared) re-runs the memo and resets the cache.
	}, [
		infiniteData?.pages.length,
		externalLoads,
		isInternal,
		searchParamsString,
	]);

	const loadsData: LoadsData = {
		summary: summaryData ?? defaultSummary,
		loads,
	};

	const isLoadingLoads = isInternal ? isLoadingInfinite : isLoadingExternal;
	const isFetchingLoads = isInternal ? isFetchingInfinite : isFetchingExternal;
	const errorLoads = isInternal ? errorInfinite : errorExternal;
	const refetchLoads = isInternal ? refetchInfinite : refetchExternal;

	return {
		loadsData,
		isLoading: isLoadingSummary || isLoadingLoads,
		isFetching: isFetchingSummary || isFetchingLoads,
		isFetchingLoads,
		isFetchingNextPage: isInternal ? isFetchingNextPage : false,
		hasNextPage: isInternal ? (hasNextPage ?? false) : false,
		fetchNextPage: isInternal
			? () => {
					fetchNextPage();
				}
			: () => {},
		error: {
			errorSummary,
			errorLoads,
		},
		refetch: {
			summary: () => {
				refetchSummary();
			},
			loads: () => {
				refetchLoads();
			},
			combined: () => {
				refetchSummary();
				refetchLoads();
			},
		},
	};
}

export const getLoadStatuses = async (
	signal?: AbortSignal,
	isInternal?: boolean,
): Promise<ShipmentStatusArray> => {
	const signalOptions = signal ? { signal } : undefined;

	const result = isInternal
		? await getAndValidateTraxAr(
				`loads/status-filters`,
				shipmentStatusArraySchema,
				signalOptions,
			)
		: await getAndValidate(`loads/status-filters`, shipmentStatusArraySchema, {
				overrideHook: (request) => {
					const selectedCustomer = JSON.parse(
						globalThis.sessionStorage.getItem("selectedCustomer") ?? "{}",
					) as CustomerDetails;
					request.headers.set(
						"Tql-Identity-Companyid",
						String(selectedCustomer?.id ?? ""),
					);
					request.headers.set("Tql-Identity-Accounttype", String(2));
				},
				signal: signal,
			});

	if (!("data" in result)) {
		throw new Error("Failed to fetch load statuses");
	}

	return result.data;
};

export const useLoadStatuses = () => {
	const { isInternal } = useDeploymentType();
	const {
		data: statuses,
		isLoading,
		error,
		refetch,
	} = useQuery({
		queryKey: ["loadStatuses"],
		queryFn: async ({ signal }) => {
			return getLoadStatuses(signal, isInternal);
		},
		enabled: isInternal,
		staleTime: Infinity,
		gcTime: Infinity,
		refetchOnWindowFocus: false,
		refetchOnMount: false,
		refetchOnReconnect: false,
	});

	return {
		statuses: statuses ?? [],
		isLoading,
		error,
		refetch,
	};
};
